import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Framework } from 'src/components/templates/Framework';
import { useApplicationTrackingAccess } from 'src/features/applicationTracking/hooks';
import { selectors as appVersionSelectors } from 'src/features/appVersion/appVersionSlice';
import { selectors as authSelectors } from 'src/features/auth/authSlice';
import useHideDocLinks from 'src/features/fdeInbox/hooks/useHideDocLinks';
import useHidePortalLinks from 'src/features/propertyFeature/hooks/useHidePortalLinks';
import { selectors as propertySelectors } from 'src/features/property/propertySlice';
import { useUserFeature } from 'src/hooks';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag/useFeatureFlag';
import { FeatureFlagKey } from 'src/ts/enums';

import { getActiveRouteOptions } from 'src/router/navOptions';

type LayoutProps = {
  children: React.ReactNode;
  isWideRoute?: boolean;
};

const PUBLIC_URL = process.env.PUBLIC_URL;

const Layout: React.FC<LayoutProps> = (props: LayoutProps) => {
  const { children, isWideRoute } = props;
  const user = useSelector(authSelectors.user);
  const appVersion = useSelector(appVersionSelectors.appVersion);
  const navigate = useNavigate();
  const { hideDocumentsTab } = useHideDocLinks();
  const { hideDocumentLinksTab } = useHidePortalLinks();
  const isDocumentLinksTabEnabled = !user?.properties?.every((property) => property.disable_dup_fd);
  const isAnalyticsAndReportingEnabled = useFeatureFlag(FeatureFlagKey.ANALYTICS_AND_REPORTING_NAV);

  const hasIdentityVerificationPropertyEnabled =
    useSelector(propertySelectors.hasIdentityVerificationPropertyEnabled.data) ?? false;
  const isDevDashboardEnabled = useUserFeature('dev_dashboard');
  const isQueueHealthEnabled = useUserFeature('queue_health');
  const isNinjaReportingEnabled = useUserFeature('ninja_reporting');
  const { hasAccess: hasApplicationTrackingAccess } = useApplicationTrackingAccess();

  const args = useMemo(
    () => ({
      version: appVersion?.frontend || 'Undefined',
      user: {
        name: user?.first_name || 'Undefined',
        logo: user?.company?.logo || `${PUBLIC_URL}/assets/images/snappt.png`
      },
      menu: getActiveRouteOptions(
        hasIdentityVerificationPropertyEnabled,
        isDocumentLinksTabEnabled && !hideDocumentsTab && !hideDocumentLinksTab,
        isAnalyticsAndReportingEnabled
      ).map((option) => {
        if (option.name === 'DevDashboard') {
          return { ...option, isVisible: isDevDashboardEnabled };
        }
        if (option.name === 'QueueHealth') {
          return { ...option, isVisible: isQueueHealthEnabled };
        }
        if (option.name === 'NinjaReporting') {
          return { ...option, isVisible: isNinjaReportingEnabled };
        }
        if (option.name === 'ApplicationTracking') {
          return { ...option, isVisible: hasApplicationTrackingAccess };
        }
        return option;
      })
    }),
    [
      appVersion?.frontend,
      user?.first_name,
      user?.company?.logo,
      hasIdentityVerificationPropertyEnabled,
      isDocumentLinksTabEnabled,
      hideDocumentsTab,
      hideDocumentLinksTab,
      isDevDashboardEnabled,
      isQueueHealthEnabled,
      isNinjaReportingEnabled,
      hasApplicationTrackingAccess,
      isAnalyticsAndReportingEnabled
    ]
  );

  return (
    <Framework
      menu={args.menu}
      version={args.version}
      user={args.user}
      onLogout={() => navigate('/logout')}
      isWide={isWideRoute}
    >
      {children}
    </Framework>
  );
};

export default Layout;
